import { Injectable, signal } from '@angular/core';

import { Cb2PreviewViewportPreset, Cb2PreviewScenarioPayload } from './cb2-preview-protocol';

/**
 * Holds the CB2 Studio preview mode state.
 *
 * Active only when the webapp is loaded inside the dashboard iframe with
 * `?cbPreview=1`. Pure signal store — no NgRx: this is ephemeral UI state
 * local to the preview adapter, not part of the persisted content runtime.
 */
@Injectable({ providedIn: 'root' })
export class Cb2PreviewStateService {
  /** True when `?cbPreview=1` is present at boot. */
  readonly isActive = signal<boolean>(false);

  /**
   * Iframe URL captured when preview starts. Later navigations are compared
   * to it so a timer cannot replace the page the dashboard opened.
   */
  readonly previewBootHref = signal<string | null>(null);

  /** Click-to-select overlay enabled by the dashboard "pick" toggle. */
  readonly pickEnabled = signal<boolean>(false);

  /** Requested viewport preset (informational — CSS sizing is driven by the iframe container). */
  readonly viewportPreset = signal<Cb2PreviewViewportPreset | null>(null);
  readonly viewportWidth = signal<number | null>(null);
  readonly viewportHeight = signal<number | null>(null);

  /** Page key currently previewed (drives forced page rendering). */
  readonly pageKey = signal<string | null>(null);

  /**
   * Root block id of the inline draft currently pushed by the dashboard.
   * In preview mode the `PageFactoryComponent` renders this block instead of
   * the marketplace route's static `data.block`, so the edited page shows even
   * though the iframe stays on its boot route.
   */
  readonly rootBlockId = signal<string | null>(null);

  /** Marketplace id pushed by the dashboard (sanity check against loaded marketplace). */
  readonly marketplaceId = signal<string | null>(null);

  /** Preview locale pushed by the dashboard. */
  readonly locale = signal<string | null>(null);

  /** Active typed preview scenarios keyed by draft bloc id. */
  readonly previewScenarios = signal<Readonly<Record<string, Cb2PreviewScenarioPayload>>>({});

  activate(bootHref?: string): void {
    this.isActive.set(true);
    if (this.previewBootHref()) {
      return;
    }
    const href = bootHref ?? (typeof window === 'undefined' ? '' : window.location.href);
    if (href) {
      this.previewBootHref.set(href);
    }
  }

  setPickEnabled(enabled: boolean): void {
    if (!this.isActive()) return;
    this.pickEnabled.set(enabled);
  }

  setViewport(preset: Cb2PreviewViewportPreset, width: number, height: number): void {
    if (!this.isActive()) return;
    this.viewportPreset.set(preset);
    this.viewportWidth.set(width);
    this.viewportHeight.set(height);
  }

  /** Updates only the device preset used by `$cases` resolution (keeps last known size). */
  setDevice(preset: Cb2PreviewViewportPreset): void {
    if (!this.isActive()) return;
    const trimmed = preset.trim();
    if (!trimmed) return;
    this.viewportPreset.set(trimmed);
  }

  setPageKey(pageKey: string | null): void {
    if (!this.isActive()) return;
    this.pageKey.set(pageKey);
  }

  setRootBlockId(rootBlockId: string | null): void {
    if (!this.isActive()) return;
    this.rootBlockId.set(rootBlockId);
  }

  setMarketplaceId(id: string | null): void {
    if (!this.isActive()) return;
    this.marketplaceId.set(id);
  }

  setLocale(locale: string | null): void {
    if (!this.isActive()) return;
    this.locale.set(locale);
  }

  setPreviewScenarios(
    scenarios: Readonly<Record<string, Cb2PreviewScenarioPayload>> | null | undefined,
  ): void {
    if (!this.isActive()) return;
    this.previewScenarios.set(scenarios ?? {});
  }
}
